// firestoreBaseApi.jsx
import {
    addDoc,
    query as buildQuery,
    collection,
    collectionGroup,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    runTransaction,
    setDoc,
    updateDoc,
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";

import { db } from "../corvusFirebase";

export function getFirebaseConsoleUrl(path) {
    return `https://console.firebase.google.com/u/0/project/corvus-backend/firestore/databases/data/${path}`;
}

/**
 * Firestore paths: These are also used by the generate-firestore-wrappers.js script to automatically generate
 * wrapper functions and hooks for use in the code. Ensure path names end in Collection or Document. Ensure the arguments
 * are within (parentheses)
 */
export const paths = {
    appCollection: () => ["apps"],
    appDocument: (appId) => [...paths.appCollection(), appId],
    droneCommandCollection: () => ["droneCommands"],
    droneCommandDocument: (droneId) => [
        ...paths.droneCommandCollection(),
        droneId,
    ],
    droneConfigCollection: () => ["droneConfigs"],
    droneConfigDocument: (droneId) => [
        ...paths.droneConfigCollection(),
        droneId,
    ],
    livestreamCollection: () => ["livestreams"],
    livestreamDocument: (droneId) => [...paths.livestreamCollection(), droneId],
    missionHandlerClusterCollection: () => ["missionHandlerClusters"],
    missionHandlerClusterDocument: (clusterName) => [
        ...paths.missionHandlerClusterCollection(),
        clusterName,
    ],

    organizationCollection: () => ["organizations"],
    organizationDocument: (orgId) => [...paths.organizationCollection(), orgId],
    locationCollection: (orgId) => [
        ...paths.organizationDocument(orgId),
        "locations",
    ],
    locationDocument: (orgId, locId) => [
        ...paths.locationCollection(orgId),
        locId,
    ],
    areasDocument: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "areas",
        "current",
    ],
    dockCollection: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "docks",
    ],
    dockDocument: (orgId, locId, dockId) => [
        ...paths.dockCollection(orgId, locId),
        dockId,
    ],
    flightCollection: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "flights",
    ],
    flightDocument: (orgId, locId, flightId) => [
        ...paths.flightCollection(orgId, locId),
        flightId,
    ],
    flyzonesDocument: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "flyzones",
        "current",
    ],
    markersDocument: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "markers",
        "current",
    ],
    batchesCollection: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "batches",
    ],
    batchesDocument: (orgId, locId, batchId) => [
        ...paths.batchesCollection(orgId, locId),
        batchId,
    ],
    locationBuilderAreasDocument: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "locationBuilder",
        "areas",
    ],
    measurementCollection: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "measurements",
    ],
    measurementDocument: (orgId, locId, measurementId) => [
        ...paths.measurementCollection(orgId, locId),
        measurementId,
    ],
    overlayCollection: (orgId, locId, measurementId) => [
        ...paths.measurementDocument(orgId, locId, measurementId),
        "overlays",
    ],
    overlayDocument: (orgId, locId, measurementId, overlayId) => [
        ...paths.measurementDocument(orgId, locId, measurementId),
        "overlays",
        overlayId,
    ],
    missionCollection: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "missions",
    ],
    missionDocument: (orgId, locId, missionId) => [
        ...paths.missionCollection(orgId, locId),
        missionId,
    ],
    resultCollection: (orgId, locId, missionId) => [
        ...paths.missionDocument(orgId, locId, missionId),
        "results",
    ],
    resultDocument: (orgId, locId, missionId, resultId) => [
        ...paths.resultCollection(orgId, locId, missionId),
        resultId,
    ],
    routineCollection: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "routines",
    ],
    routineDocument: (orgId, locId, routineId) => [
        ...paths.routineCollection(orgId, locId),
        routineId,
    ],
    taskCollection: (orgId, locId) => [
        ...paths.locationDocument(orgId, locId),
        "tasks",
    ],
    taskDocument: (orgId, locId, taskId) => [
        ...paths.taskCollection(orgId, locId),
        taskId,
    ],

    payloadCollection: () => ["payloads"],
    payloadDocument: (payloadId) => [...paths.payloadCollection(), payloadId],
    userFeedbackCollection: () => ["userFeedbacks"],
    userFeedbackDocument: (userId) => [
        ...paths.userFeedbackCollection(),
        userId,
    ],
    changelogCollection: () => ["changelogs"],
    changelogDocument: (docId) => [...paths.changelogCollection(), docId],
    routeplannerCollection: () => ["routeplanners"],
    routeplannerDocument: (docId) => [...paths.routeplannerCollection(), docId],
    unauthorizedAccessCollection: () => ["unauthorizedAccess"],
    unauthorizedAccessDocument: (docId) => [
        ...paths.unauthorizedAccessCollection(),
        docId,
    ],

    tagsDocument: () => ["corvus", "tags"],
    feedbackOptionsDocument: () => ["corvus", "feedback"],
    versionsDocument: () => ["corvus", "versions"],
    unavailableDroneNamesDocument: () => ["corvus", "unavailableDroneNames"],
    modelsCollection: () => ["models"],
    modelsDocument: (modelId) => [...paths.modelsCollection(), modelId],
};

/**
 * HELPERS AND FACTORIES
 */
function getCollectionRef(pathArray) {
    return collection(db, ...pathArray);
}

function getDocumentRef(pathArray) {
    return doc(db, ...pathArray);
}

/**
 * Wrap a Firestore ref with a little logger
 */
function wrapWithLogging(ref, alternativePathName = "") {
    const path = ref.path || alternativePathName;
    const log = (method, extra = "") =>
        console.debug(`[Firestore] ${method} → ${path}${extra}`);
    const logError = (method, err) =>
        console.error(`[Firestore] ${method} ✖ ${path}`, err);

    return { ref, log, logError };
}

function buildCollectionMethods(ref, log, logError) {
    const list = async (constraints = []) => {
        log(
            "list",
            constraints.length ? ` ${JSON.stringify(constraints)}` : "",
        );
        try {
            const q = constraints.length
                ? buildQuery(ref, ...constraints)
                : ref;
            const snap = await getDocs(q);
            return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (err) {
            logError("list", err);
            throw err;
        }
    };

    const subscribeList = (cb, constraints = []) => {
        log(
            "subscribeList",
            constraints.length ? ` ${JSON.stringify(constraints)}` : "",
        );
        const q = constraints.length ? buildQuery(ref, ...constraints) : ref;
        return onSnapshot(
            q,
            (snap) => {
                log("onSnapshotList", ` (${snap.size} docs)`);
                cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            },
            (err) => {
                logError("subscribeList", err);
            },
        );
    };

    const listMap = async (constraints = []) => {
        const docs = await list(constraints);
        return docs.reduce((m, doc) => {
            m[doc.id] = doc;
            return m;
        }, {});
    };

    const subscribeMap = (cb, constraints = []) =>
        subscribeList(
            (docs) =>
                cb(
                    docs.reduce((m, doc) => {
                        m[doc.id] = doc;
                        return m;
                    }, {}),
                ),
            constraints,
        );

    return { list, subscribeList, listMap, subscribeMap };
}

export function createDocumentApi(pathFn) {
    return (...args) => {
        const { ref, log, logError } = wrapWithLogging(
            getDocumentRef(pathFn(...args)),
        );

        return {
            path: ref.path,
            get: async () => {
                log("get");
                try {
                    const snap = await getDoc(ref);
                    if (snap.exists()) {
                        return { id: snap.id, ...snap.data() };
                    } else {
                        throw Error(`Document ${ref.path} does not exist`);
                    }
                } catch (err) {
                    logError("get", err);
                    throw err;
                }
            },
            subscribe: (cb) => {
                log("subscribe");
                return onSnapshot(
                    ref,
                    (snap) => {
                        log(
                            "onSnapshot",
                            snap.exists() ? "" : " (does not exist)",
                        );
                        cb(
                            snap.exists()
                                ? { id: snap.id, ...snap.data() }
                                : null,
                        );
                    },
                    (err) => {
                        logError("subscribe", err);
                    },
                );
            },
            set: async (data, options = {}) => {
                log("set", options ? `, ${JSON.stringify(options)}` : "");
                try {
                    await setDoc(ref, data, options);
                } catch (err) {
                    logError("set", err);
                    throw err;
                }
            },
            update: async (updates) => {
                log("update");
                try {
                    await updateDoc(ref, updates);
                } catch (err) {
                    logError("update", err);
                    throw err;
                }
            },
            delete: async () => {
                log("delete");
                try {
                    await deleteDoc(ref);
                } catch (err) {
                    logError("delete", err);
                    throw err;
                }
            },
            transactionUpdate: (updateFn) =>
                runTransaction(db, async (tx) => {
                    log("transactionUpdate");
                    try {
                        const snap = await tx.get(ref);
                        const current = snap.exists()
                            ? { id: snap.id, ...snap.data() }
                            : null;
                        const updates = await updateFn(current);
                        tx.update(ref, updates);
                        return updates;
                    } catch (err) {
                        logError("transactionUpdate", err);
                        throw err;
                    }
                }),
        };
    };
}

export function createCollectionApi(pathFn) {
    return (...args) => {
        const { ref, log, logError } = wrapWithLogging(
            getCollectionRef(pathFn(...args)),
        );
        const methods = buildCollectionMethods(ref, log, logError);
        const add = async (data) => {
            log("add");
            try {
                const docRef = await addDoc(ref, data);
                return docRef.id;
            } catch (err) {
                logError("add", err);
                throw err;
            }
        };
        return { ...methods, add, path: ref.path };
    };
}

export function createCollectionGroupApi(segment) {
    const { ref, log, logError } = wrapWithLogging(
        collectionGroup(db, segment),
        `collectiongroup:${segment}`,
    );
    return buildCollectionMethods(ref, log, logError);
}

/**
 * Hook for subscribing to—or once-fetching—a single document.
 *
 * @param apiFn   Function returning { subscribe(cb): unsubFn, get(): Promise }
 * @param args    Array of arguments to pass to apiFn
 * @param options.mode  'live' (default) or 'once'
 */
export function useDocument(apiFn, args, options = {}) {
    const { mode = "live" } = options;
    // biome-ignore lint/correctness/useExhaustiveDependencies: Stabilize args by value to avoid resubscribing when callers create equivalent arrays.
    const memoArgs = useMemo(() => args, [JSON.stringify(args)]);

    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();

    useEffect(() => {
        // If any path param is undefined/null, skip until args are complete
        if (memoArgs.some((arg) => arg === undefined || arg === null)) {
            console.debug(
                "[Firestore] useDocument → skipping fetch, args contain undefined or null",
                memoArgs,
            );
            return;
        }

        setIsLoading(true);
        setError(undefined);

        if (mode === "live") {
            let unsub;
            try {
                unsub = apiFn(...memoArgs).subscribe((doc) => {
                    setData(doc);
                    setIsLoading(false);
                    setError(undefined);
                });
            } catch (err) {
                setError(err);
                setIsLoading(false);
            }
            return () => unsub?.();
        } else if (mode === "once") {
            apiFn(...memoArgs)
                .get()
                .then((doc) => {
                    setData(doc);
                    setError(undefined);
                })
                .catch((err) => {
                    setError(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
            return;
        } else {
            throw new Error(
                `Invalid mode "${mode}" - expected "live" or "once"`,
            );
        }
    }, [apiFn, memoArgs, mode]);

    return [data, isLoading, error];
}

/**
 * Hook for subscribing to—or once-fetching—a collection,
 * returning either a list or a map of documents.
 *
 * @param apiFn      Function returning { subscribeList, subscribeMap, list, listMap }
 * @param args       Array of arguments to pass to apiFn
 * @param constraints Array of query constraints
 * @param options    { mode?: 'live'|'once', type?: 'list'|'map' }
 */
export function useCollection(apiFn, args, constraints = [], options = {}) {
    const { mode = "live", type = "list" } = options;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Stabilize args by value to avoid resubscribing when callers create equivalent arrays.
    const memoArgs = useMemo(() => args, [JSON.stringify(args)]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: Stabilize constraints by value to avoid resubscribing when callers create equivalent arrays.
    const memoConstraints = useMemo(
        () => constraints,
        [JSON.stringify(constraints)],
    );

    const [data, setData] = useState(type === "map" ? {} : []);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();

    useEffect(() => {
        // Skip until all path params are defined
        if (memoArgs.some((arg) => arg === undefined || arg === null)) {
            console.debug(
                "[Firestore] useCollection → skipping fetch, args contain undefined or null",
                memoArgs,
            );
            return;
        }

        setIsLoading(true);
        setError(undefined);

        if (mode !== "live" && mode !== "once") {
            throw new Error(
                `Invalid mode "${mode}" - expected "live" or "once"`,
            );
        }
        if (type !== "list" && type !== "map") {
            throw new Error(
                `Invalid type "${type}" - expected "list" or "map"`,
            );
        }

        if (mode === "live") {
            let unsub;
            try {
                if (type === "list") {
                    unsub = apiFn(...memoArgs).subscribeList((list) => {
                        setData(list);
                        setIsLoading(false);
                        setError(undefined);
                    }, memoConstraints);
                } else {
                    unsub = apiFn(...memoArgs).subscribeMap((map) => {
                        setData(map);
                        setIsLoading(false);
                        setError(undefined);
                    }, memoConstraints);
                }
            } catch (err) {
                setError(err);
                setIsLoading(false);
            }
            return () => unsub?.();
        } else {
            // once‐fetch
            const fetcher =
                type === "list"
                    ? apiFn(...memoArgs).list(memoConstraints)
                    : apiFn(...memoArgs).listMap(memoConstraints);

            fetcher
                .then((res) => {
                    setData(res);
                    setError(undefined);
                })
                .catch((err) => {
                    setError(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
            return;
        }
    }, [apiFn, memoArgs, memoConstraints, mode, type]);

    return [data, isLoading, error];
}

/**
 * Hook for subscribing to—or once-fetching—a collection group,
 * returning either a list or a map of documents.
 *
 * @param apiObj      An object with subscribeList, subscribeMap, list, listMap methods
 * @param constraints Array of query constraints
 * @param options     { mode?: 'live'|'once', type?: 'list'|'map' }
 */
export function useCollectionGroup(apiObj, constraints = [], options = {}) {
    const { mode = "live", type = "list" } = options;

    // biome-ignore lint/correctness/useExhaustiveDependencies: Stabilize constraints by value to avoid resubscribing when callers create equivalent arrays.
    const memoConstraints = useMemo(
        () => constraints,
        [JSON.stringify(constraints)],
    );

    const [data, setData] = useState(type === "map" ? {} : []);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState();

    useEffect(() => {
        setIsLoading(true);
        setError(undefined);

        if (mode !== "live" && mode !== "once") {
            throw new Error(
                `Invalid mode "${mode}" - expected "live" or "once"`,
            );
        }
        if (type !== "list" && type !== "map") {
            throw new Error(
                `Invalid type "${type}" - expected "list" or "map"`,
            );
        }

        if (mode === "live") {
            let unsub;
            try {
                if (type === "list") {
                    unsub = apiObj.subscribeList((list) => {
                        setData(list);
                        setIsLoading(false);
                        setError(undefined);
                    }, memoConstraints);
                } else {
                    unsub = apiObj.subscribeMap((map) => {
                        setData(map);
                        setIsLoading(false);
                        setError(undefined);
                    }, memoConstraints);
                }
            } catch (err) {
                setError(err);
                setIsLoading(false);
            }
            return () => unsub?.();
        } else {
            // once
            if (type === "list") {
                apiObj.list(memoConstraints).then(setData);
            } else {
                apiObj.listMap(memoConstraints).then(setData);
            }
            return;
        }
    }, [apiObj, memoConstraints, mode, type]);

    return [data, isLoading, error];
}
